import { useRef, useState } from "react"
import type * as React from "react"
import { CalendarDays } from "lucide-react"

import { UnderlineInput } from "@/components/ui/underline-input"
import { cn } from "@/lib/utils"

export interface UnderlineDateInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Extra classes for the visible text field (the wrapper takes className). */
  inputClassName?: string
}

/** A complete, real calendar day — the only non-empty value this field emits. */
function isIsoDay(value: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value))
  )
}

/**
 * Underlined date field that reads and writes ISO `yyyy-mm-dd`.
 *
 * A native `type="date"` input displays in the browser's locale — mm/dd/yyyy
 * on many machines — while dates here are ZA-style ISO. So the visible field
 * is a text input holding the ISO string, with the native calendar available
 * behind the button via a hidden date input sharing the value.
 *
 * Partial values stay local while typing: `onChange` only fires with a valid
 * complete day or an empty string, preserving the native input's guarantee
 * that callers never see half a date.
 */
export function UnderlineDateInput(props: UnderlineDateInputProps) {
  const {
    placeholder = "yyyy-mm-dd",
    className,
    inputClassName,
    value,
    onChange,
    onBlur,
    min,
    max,
    ...rest
  } = props
  const pickerRef = useRef<HTMLInputElement>(null)
  // The in-progress text while typing; null means "mirror the value prop".
  const [draft, setDraft] = useState<string | null>(null)

  const committed = typeof value === "string" ? value : ""
  const shown = draft ?? committed

  const handleTextChange: React.ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const next = event.target.value
    if (next === "" || isIsoDay(next)) {
      setDraft(null)
      onChange?.(event)
    } else {
      setDraft(next)
    }
  }

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = (event) => {
    // Whatever didn't become a real date snaps back to the last good value.
    setDraft(null)
    onBlur?.(event)
  }

  return (
    <div className={cn("relative flex items-center gap-1.5", className)}>
      <UnderlineInput
        {...rest}
        type="text"
        inputMode="numeric"
        maxLength={10}
        placeholder={placeholder}
        value={shown}
        onChange={handleTextChange}
        onBlur={handleBlur}
        className={cn("min-w-0 flex-1", inputClassName)}
      />
      {/* The real date input sits transparently over the icon, so a tap on
          the calendar IS a tap on a native date input — iOS opens its picker
          from the direct tap (programmatic focus()/showPicker() are ignored
          or unsupported there), while desktop gets showPicker() from the
          same click. The overlay extends past the icon (-inset-*) for a
          finger-sized target. */}
      <span className="relative shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
        <CalendarDays className="h-4 w-4" aria-hidden />
        <input
          ref={pickerRef}
          type="date"
          tabIndex={-1}
          aria-label="Pick a date"
          value={isIsoDay(committed) ? committed : ""}
          min={min}
          max={max}
          onClick={(event) => {
            try {
              event.currentTarget.showPicker()
            } catch {
              // iOS: the direct tap opens the native picker by itself.
            }
          }}
          onChange={(event) => {
            setDraft(null)
            onChange?.(event)
          }}
          className="absolute -inset-2 h-[calc(100%+1rem)] w-[calc(100%+1rem)] cursor-pointer opacity-0"
        />
      </span>
    </div>
  )
}
